import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Card } from "@/components/ui/Card";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { ProgressRing } from "@/components/ui/ProgressRing";
import { Skeleton } from "@/components/ui/Skeleton";
import { calculatePlanProgress } from "@/lib/progress";
import { formatDate, formatNumber } from "@/lib/format";
import { ConfidenceChip } from "@/components/progress/ConfidenceChip";
import {
  expenditureLabel,
  formatGoalDateRange,
  formatWeeklyChange,
  formatWeeksRange,
  goalProgressPercent,
  type EngineSnapshot,
} from "@/components/progress/utils";
import type { Doc } from "../../convex/_generated/dataModel";

export function PlanProgressCard({ profile }: { profile: Doc<"users"> | null | undefined }) {
  const engineState = useQuery(api.engine.getEngineState, {});

  if (profile === undefined || engineState === undefined) {
    return <Skeleton className="h-40 rounded-2xl" data-testid="plan-progress-loading" />;
  }
  if (profile === null) return null;
  if (engineState === null) return <LegacyPlanProgress profile={profile} />;
  return <EnginePlanProgress profile={profile} state={engineState} />;
}

function EnginePlanProgress({ profile, state }: { profile: Doc<"users">; state: EngineSnapshot }) {
  const goalPct = goalProgressPercent(
    profile.startingWeightKg ?? profile.weightKg ?? null,
    state.trendKg,
    profile.targetWeightKg ?? null,
  );
  const weeksRange = formatWeeksRange(state.weeksMin, state.weeksMax);
  const goalDates = formatGoalDateRange(state.goalDateMin, state.goalDateMax);

  return (
    <Card className="flex flex-col gap-3 border-accent/20 bg-accent-light/45 p-4" data-testid="plan-progress-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">Your programme</p>
          <h2 className="mt-1 text-base font-semibold text-text">
            Daily target {state.targetKcal !== null ? `${formatNumber(state.targetKcal)} kcal` : "—"}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {profile.targetWeightKg !== undefined && (
            <span className="text-right text-sm font-semibold text-accent">{profile.targetWeightKg} kg goal</span>
          )}
          <ProgressRing value={goalPct ?? 0} size={68} stroke={7} color="#2f855a">
            <span className="text-xs font-bold text-accent">{goalPct === null ? "—" : `${goalPct}%`}</span>
          </ProgressRing>
        </div>
      </div>
      <ProgressBar value={goalPct ?? 0} color="#2f855a" />
      {state.restricted ? (
        <p
          className="rounded-xl bg-warning/10 px-3 py-2 text-xs leading-relaxed text-warning"
          data-testid="plan-restricted-notice"
        >
          Your target has reached the safe minimum — the plan will not reduce calories further. Focus on consistency, not bigger deficits.
        </p>
      ) : (
        <>
          <div className="flex justify-between gap-2 text-xs text-text-muted">
            <span>
              Weekly change <strong className="text-text">{formatWeeklyChange(state.weeklyChangeKg)}</strong>
            </span>
            <span>
              <strong className="text-text">{weeksRange ?? "Timeline calibrating"}</strong>
            </span>
          </div>
          <p className="text-xs text-text-muted">
            Projected goal window: <strong className="text-text">{goalDates ?? "—"}</strong>
          </p>
        </>
      )}
      <div className="flex items-center justify-between gap-2 rounded-xl bg-surface px-3 py-2 text-xs text-text-muted">
        <span>
          {expenditureLabel(state.expenditureKind)}{" "}
          <strong className="text-text">
            {state.expenditureKcal !== null ? `${formatNumber(state.expenditureKcal)} kcal` : "—"}
          </strong>
        </span>
        <ConfidenceChip level={state.expenditureConfidence} testId="plan-expenditure-confidence" />
      </div>
    </Card>
  );
}

/** Fallback render when the engine has no snapshot yet (e.g. before first recalculate). */
function LegacyPlanProgress({ profile }: { profile: Doc<"users"> }) {
  const progress = calculatePlanProgress(profile);
  if (progress === null) return null;

  return (
    <Card className="flex flex-col gap-3 border-accent/20 bg-accent-light/45 p-4" data-testid="plan-progress-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">60-day plan · Phase {progress.phaseNumber}</p>
          <h2 className="mt-1 text-base font-semibold text-text">{progress.daysRemaining} days left in this phase</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-right text-sm font-semibold text-accent">{progress.targetWeightKg} kg goal</span>
          <ProgressRing value={progress.phaseProgressPercent} size={68} stroke={7} color="#2f855a">
            <span className="text-xs font-bold text-accent">{Math.round(progress.phaseProgressPercent)}%</span>
          </ProgressRing>
        </div>
      </div>
      <ProgressBar value={progress.phaseProgressPercent} color="#2f855a" />
      <div className="flex justify-between text-xs text-text-muted">
        <span>{progress.daysIntoPhase} days completed</span>
        <span>Projected {progress.projectedLossKg} kg / phase</span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-text-muted">
        <div className="rounded-xl bg-surface px-3 py-2">BMR <strong className="text-text">{progress.bmrKcal ?? "—"}</strong> kcal</div>
        <div className="rounded-xl bg-surface px-3 py-2">TDEE <strong className="text-text">{progress.tdeeKcal ?? "—"}</strong> kcal</div>
        <div className="rounded-xl bg-surface px-3 py-2">BMI <strong className="text-text">{progress.bmi ?? "—"}</strong></div>
        <div className="rounded-xl bg-surface px-3 py-2">Target timing <strong className="text-text">{progress.estimatedWeeksToTarget === null ? "—" : progress.estimatedWeeksToTarget === 0 ? "On target" : `${progress.estimatedWeeksToTarget} weeks`}</strong></div>
      </div>
      <p className="text-xs text-text-muted">Projected target date: <strong className="text-text">{progress.projectedTargetDate === null ? "—" : formatDate(progress.projectedTargetDate)}</strong></p>
    </Card>
  );
}
